import cn from 'classnames';
import { ReactNode, useState } from 'react';
import { Form } from 'react-bootstrap';
import { MdClose } from 'react-icons/md';
import { Appearance, Design } from '@Components/designConstants';
import { IconButton } from '@src/Components/IconButton';
import { Comparison } from './request-form-constants';
import { comparisonDollars, comparisonPct, isComparisonPctInvalid } from './request-form-utils';

interface ComparisonBudgetRowProps {
  cp: Comparison;
  primaryNum: number;
  onChangePct: (pct: string) => void;
  onRemove: () => void;
  removable: boolean;
}

export const ComparisonBudgetRow = ({
  cp,
  primaryNum,
  onChangePct,
  onRemove,
  removable
}: ComparisonBudgetRowProps): ReactNode => {
  const pctNum = Number(cp.pct);
  const over = isComparisonPctInvalid(cp.pct);
  const derivedDollars = comparisonDollars(primaryNum, pctNum);
  // Shows exactly what's typed while focused, instead of a value re-derived
  // from pct that could drift from it on each keystroke.
  const [dollarDraft, setDollarDraft] = useState<string | null>(null);
  // Reset the draft if primaryNum changes from outside so it can't go stale.
  const [lastPrimaryNum, setLastPrimaryNum] = useState(primaryNum);
  if (primaryNum !== lastPrimaryNum) {
    setLastPrimaryNum(primaryNum);
    setDollarDraft(null);
  }

  return (
    <div className={cn('req-comparison', { over, locked: cp.locked })}>
      <div className='req-money req-pct'>
        <Form.Control
          type='number'
          value={cp.pct}
          min={0}
          max={200}
          disabled={cp.locked}
          onChange={e => onChangePct(e.target.value)}
        />
        <span>% of primary</span>
      </div>
      <span className='req-eq'>=</span>
      <div className='req-money req-derived'>
        <span>$</span>
        <Form.Control
          type='number'
          value={dollarDraft ?? derivedDollars}
          min={0}
          disabled={cp.locked}
          onChange={e => {
            const raw = e.target.value;
            setDollarDraft(raw);
            const dollars = Number(raw);
            // Only commit a value that parses, so "", "-", etc. mid-edit don't zero the pct.
            if (raw !== '' && Number.isFinite(dollars)) {
              onChangePct(String(comparisonPct(dollars, primaryNum)));
            }
          }}
          onBlur={() => setDollarDraft(null)}
        />
      </div>
      {removable && (
        <IconButton appearance={Appearance.EMPTY} design={Design.NEUTRAL} onClick={onRemove}>
          <MdClose />
        </IconButton>
      )}
      {over && <span className='req-error'>Must be 0-200% of primary</span>}
    </div>
  );
};
